import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { LegalRequestForm } from "@/components/LegalRequestForm";

const S = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-sm sm:text-base text-gray-600 leading-relaxed">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-paper">
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="terms-home-link" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-trust flex items-center justify-center">
              <ShieldCheck size={16} className="text-white" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight">ProofBuy</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-trust transition-colors duration-200">
            <ArrowLeft size={15} /> Back
          </Link>
        </div>
      </header>

      <main data-testid="terms-page" className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-trust">Legal</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight">Terms of Use</h1>
        <p className="mt-3 text-sm text-gray-500">Last updated: 24 August 2026</p>
        <p className="mt-5 text-base text-gray-600 leading-relaxed">
          These are the rules for using ProofBuy (proofbuy.in). They're written to be readable. By using the site,
          submitting a review, or joining the waitlist, you agree to them.
        </p>

        <S title="1. What ProofBuy is">
          <p>
            ProofBuy publishes users' factual, first-hand purchase experiences of digital products and online
            courses, each backed by a payment screenshot and a product screenshot. It is a record of what buyers
            paid and what they received — nothing more.
          </p>
        </S>

        <S title="2. The core rule: facts, not accusations">
          <p>
            ProofBuy is <b>not</b> a place to accuse, defame, or call anyone a "fraud" or a "scammer." You agree to
            post only factual accounts of your own real purchases: what the ad said, what you paid, what you got.
            The receipt speaks; labels and name-calling don't belong here.
          </p>
        </S>

        <S title="3. Your promises when you submit">
          <ul className="list-disc pl-5 space-y-2">
            <li>The purchase you're describing really happened, and you were the buyer.</li>
            <li>The payment and product screenshots you upload are <b>genuine and your own</b> — not edited to mislead, not someone else's, not fabricated.</li>
            <li>What you write is accurate to the best of your knowledge.</li>
          </ul>
          <p>Submitting fake proof or invented experiences can get your content removed and your account closed.</p>
        </S>

        <S title="4. What we do — and don't — guarantee">
          <p>
            ProofBuy verifies that a review carries the required proofs and meets our factual-wording rules before
            it's published. We do <b>not</b> guarantee that any review is complete, typical, or correct. Reviews are
            individual buyers' experiences and opinions. Always read them as one data point, not the final word.
          </p>
        </S>

        <S title="5. Sellers can reply">
          <p>
            Sellers named in a review have the right to respond. Their response appears beside the review, clearly
            labeled, after they confirm their email and the response meets the same factual-wording rules. Both
            sides get heard — that's the design.
          </p>
        </S>

        <S title="6. Content removal">
          <p>
            We may remove (or refuse to publish) content that is abusive, false, defamatory, or lacks the required
            proof — or that breaks these terms in any other way. We may also suspend accounts that repeatedly break
            the rules.
          </p>
        </S>

        <S title="7. A neutral platform — and the limits of our liability">
          <p>
            ProofBuy is a neutral platform hosting user-generated content. Reviews and seller responses are written
            by users, not by us, and we're not responsible for what users post. To the extent the law allows,
            ProofBuy is not liable for any loss — including a purchase you made or skipped because of something you
            read here — arising from user content. Nothing in these terms limits rights you can't legally give up
            under Indian law.
          </p>
        </S>

        <S title="8. Governing law">
          <p>These terms are governed by the laws of India. Disputes are subject to the jurisdiction of Indian courts.</p>
        </S>

        <S title="9. Disputes and takedown requests">
          <p>
            If you believe a review about you or your product is false, lacks genuine proof, or breaks these terms,
            email <a href="mailto:support@proofbuy.in" className="text-trust font-semibold">support@proofbuy.in</a> with
            the review details and your side. You can also always respond publicly beside the review — see Section 5.
          </p>
        </S>

        <S title="10. Changes to these terms">
          <p>
            If we change these terms, we'll update the date at the top and, for significant changes, give notice on
            the site or by email before they take effect.
          </p>
        </S>

        <LegalRequestForm />
      </main>
    </div>
  );
}
